import { HEADER_BYTES, OK, assertField, checkHeader, putHeader, putU16, putU32, readU16, readU32, type DecodeVerdict } from "./binary.ts";
import { MessageType } from "./catalogue.ts";
import { MAX_U16, MAX_U32 } from "./limits.ts";

/**
 * The `act` frame (ADR-074 decision 2, PROTOCOL.md 5.6): 13 bytes, client to server, a request to use an ability. It
 * carries the input it follows, the ability's number, the selected target's handle and the aim, and never a position,
 * a time or a result (ADR-073 decision 4). Every value of every field is well formed, so the decoder's only faults are
 * the length, the version and the type; it never throws (ADR-030).
 */

/**
 * Total frame length.
 */
export const ACT_BYTES = 13;

/**
 * A decoded or to-be-encoded `act`, reused across frames. `afterSeq` is the `seq` of the last `input` the page sent
 * before it (0 before any), `ability` the ability's place in the rules' abilities counting from 1, `target` a handle or
 * `NO_HANDLE`, and `aim` a direction in 65,536ths of a turn, 0 right and growing clockwise on the screen.
 */
export interface ActRecord {
  afterSeq: number;
  ability: number;
  target: number;
  aim: number;
}

/**
 * A fresh record for `decodeAct` to fill.
 *
 * @returns A zeroed record.
 */
export function newActRecord(): ActRecord {
  return { afterSeq: 0, ability: 0, target: 0, aim: 0 };
}

/**
 * Encodes an `act` into a caller-owned buffer.
 *
 * @param buffer Destination, at least `ACT_BYTES` long.
 * @param act The values.
 * @returns Bytes written, always `ACT_BYTES`.
 * @throws Error when the buffer is too small or a value does not fit its field.
 */
export function encodeAct(buffer: Uint8Array, act: Readonly<ActRecord>): number {
  if (buffer.length < ACT_BYTES) {
    throw new Error(`buffer of ${buffer.length} bytes cannot hold a ${ACT_BYTES}-byte act`);
  }
  assertField(act.afterSeq, MAX_U32, `act afterSeq ${act.afterSeq}`);
  assertField(act.ability, MAX_U16, `act ability ${act.ability}`);
  assertField(act.target, MAX_U16, `act target ${act.target}`);
  assertField(act.aim, MAX_U16, `act aim ${act.aim}`);
  putHeader(buffer, MessageType.Act);
  putU32(buffer, HEADER_BYTES, act.afterSeq);
  putU16(buffer, HEADER_BYTES + 4, act.ability);
  putU16(buffer, HEADER_BYTES + 6, act.target);
  putU16(buffer, HEADER_BYTES + 8, act.aim);
  return ACT_BYTES;
}

/**
 * Decodes an `act` frame into a record. Never throws: a wrong length or type is malformed and a wrong version a
 * mismatch; every field value is well formed, so an ability number the rules do not hold is the map server's to drop.
 *
 * @param frame The whole frame.
 * @param out Record to fill; untouched unless the verdict is `ok`.
 * @returns The verdict.
 */
export function decodeAct(frame: Uint8Array, out: ActRecord): DecodeVerdict {
  const header = checkHeader(frame, MessageType.Act, ACT_BYTES);
  if (header.kind !== "ok") {
    return header;
  }
  out.afterSeq = readU32(frame, HEADER_BYTES);
  out.ability = readU16(frame, HEADER_BYTES + 4);
  out.target = readU16(frame, HEADER_BYTES + 6);
  out.aim = readU16(frame, HEADER_BYTES + 8);
  return OK;
}
